import React, { useState, useMemo, useEffect, useCallback } from 'react'
import useQuery from 'shared/hooks/useQuery'
import useIndexedDb from 'shared/hooks/useIndexedDb'
import styled from 'styled-components'
import Box from 'shared/components/Box'
import queryString from 'qs'
import { Tooltip, TooltipReference, TooltipArrow, useTooltipState } from 'reakit/Tooltip'

type ProtectedImageProps = {
  style?: any
  src: string
  token?: string | null
  tokenType?: string | null
  defaultSrc?: string
  className?: string
  displayTooltip?: boolean
  alt?: string
  onError?: any
}

export function ProtectedImage(props: ProtectedImageProps) {
  const {
    style = {},
    src = '',
    token = '',
    tokenType = '',
    defaultSrc = '',
    className = '',
    displayTooltip,
    alt = '',
    onError = {},
  } = props
  const [imageUrl, setImageUrl] = useState(defaultSrc)
  const [isSkip, setIsSkip] = useState(true)
  const [urlImgSrc, setUrlImgSrc] = useState('')
  const tooltip = useTooltipState({ animated: 250 })
  const [indexedDBHandler] = useIndexedDb({ tableNames: ['images'] })
  const isStorageS3 = useMemo(() => process.env.REACT_APP_ASSET_STORAGE == 's3', [])
  const onFetchSuccess = useCallback(
    async (data: any) => {
      if (data) {
        if (isStorageS3) {
          setImageUrl(data.url)
        } else {
          await indexedDBHandler.putValue('images', { image_blob: data, name: src })
          const url = window.URL.createObjectURL(data)

          setImageUrl(url)
        }
      }
    },
    [isStorageS3, indexedDBHandler, setImageUrl, src],
  )

  const photoQueryString = queryString.stringify({
    token,
    token_type: tokenType,
  })

  const [imageState, imageQueryHandler] = useQuery(
    {
      url: token && tokenType ? `asset/public/${urlImgSrc}?${photoQueryString}` : `/asset/${urlImgSrc}`,
      responseType: isStorageS3 ? 'json' : 'blob',
    },
    {
      initialData: null,
      isBase: false,
      skip: isSkip,
      onFetchSuccess,
    },
  )

  const checkImage = useCallback(async () => {
    const indexedImage = isStorageS3 ? false : await indexedDBHandler.getValue('images', 'name', src)
    if (indexedImage) {
      const url = window.URL.createObjectURL(indexedImage.image_blob)
      setImageUrl(url)
    } else if (src) {
      if (src.includes('data:image')) setImageUrl(src)
      else {
        setUrlImgSrc(src)
        setIsSkip(false)
        imageQueryHandler.refetch()
      }
    }
  }, [src, indexedDBHandler, setUrlImgSrc, setImageUrl, setIsSkip, imageQueryHandler, isStorageS3])

  useEffect(() => {
    checkImage()
  }, [src])

  const displayImage = useMemo(
    () => (
      <>
        {!displayTooltip && (
          <StyledImageBox style={style} src={imageUrl} alt={alt} className={className} onError={() => onError} />
        )}
      </>
    ),
    [displayTooltip, style, imageUrl, alt, className],
  )

  return (
    <>
      {displayTooltip && alt && (
        <ProtectedImageContainer>
          <TooltipReference
            {...tooltip}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignContent: 'center',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <StyledImageBox style={style} src={imageUrl} alt={alt} className={className} onError={() => onError} />
          </TooltipReference>

          <Tooltip {...tooltip} style={{ background: 'none', padding: 0 }}>
            <StyledImageTooltip>
              <TooltipArrow {...tooltip} />
              {alt}
            </StyledImageTooltip>
          </Tooltip>
        </ProtectedImageContainer>
      )}
      {displayImage}
    </>
  )
}

const StyledImageBox = styled.img`
  max-width: 100%;
  height: auto;
`

const StyledImageTooltip = styled.div`
  background-color: rgba(33, 33, 33, 0.9);
  color: white;
  padding: 8px;
  border-radius: 4px;
  max-width: 390px;
  transition: opacity 150ms ease-in-out, transform 150ms ease-in-out;
  opacity: 0;
  transform-origin: top center;
  transform: translate3d(0, -10px, 0);
  [data-enter] & {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`
const ProtectedImageContainer = styled(Box)`
  width: 100%;
  height: 100%;
  text-align: center;
`

export default ProtectedImage
